import Table from '@/components/TableComponents/Table';
import useI18n from '@/hooks/useI18n';
import { formatCount } from '@/utils/numberUtils';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter } from '@/utils/caseUtils';
import { UserContext } from '@/context/UserContext';
import InfoTooltip from '@/components/common/InfoTooltip';
import { ROUTES } from '@/routes/constants';

const sampleData = [
  {
    key: '1',
    userId: 'dummy-1',
    userName: 'Samuel_W',
    profileImage: '/assets/profilePicture.png',
    postCount: 3,
    socialScore: { totalScore: 820 },
    platform: 'instagram',
    platforms: ['instagram'],
    views: 540000,
  },
  {
    key: '2',
    userId: 'dummy-2',
    userName: 'WoiW',
    profileImage: '/assets/profilePicture.png',
    postCount: 1,
    socialScore: { totalScore: 740 },
    platform: 'youtube',
    platforms: ['youtube'],
    views: 480000,
  },
  {
    key: '3',
    userId: 'dummy-3',
    userName: 'Jane_Doe',
    profileImage: '/assets/profilePicture.png',
    postCount: 2,
    socialScore: { totalScore: 690 },
    platform: 'tiktok',
    platforms: ['tiktok'],
    views: 620000,
  },
  {
    key: '4',
    userId: 'dummy-4',
    userName: 'Alex_Smith',
    profileImage: '/assets/profilePicture.png',
    postCount: 1,
    socialScore: { totalScore: 610 },
    platform: 'instagram',
    platforms: ['instagram'],
    views: 310000,
  },
  {
    key: '5',
    userId: 'dummy-5',
    userName: 'Emma_Lee',
    profileImage: '/assets/profilePicture.png',
    postCount: 3,
    socialScore: { totalScore: 780 },
    platform: 'youtube',
    platforms: ['youtube'],
    views: 430000,
  },
  {
    key: '6',
    userId: 'dummy-6',
    userName: 'Mark_Taylor',
    profileImage: '/assets/profilePicture.png',
    postCount: 2,
    socialScore: { totalScore: 560 },
    platform: 'tiktok',
    platforms: ['tiktok'],
    views: 390000,
  },
  {
    key: '7',
    userId: 'dummy-7',
    userName: 'Sarah_Connor',
    profileImage: '/assets/profilePicture.png',
    postCount: 1,
    socialScore: { totalScore: 870 },
    platform: 'instagram',
    platforms: ['instagram', 'youtube'],
    views: 530000,
  },
];

// Proper brand casing for platform labels; capitalizeFirstLetter would yield
// "Tiktok"/"Youtube" instead of the Figma's "TikTok"/"YouTube".
const PLATFORM_LABELS: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

const formatPlatformLabel = (platform: string): string =>
  PLATFORM_LABELS[platform?.toLowerCase()] ?? capitalizeFirstLetter(platform);

const TopPerformingInfluencersComponent = ({
  topPerformingInfluencers,
}: any) => {
  console.log('topPerformingInfluencers', topPerformingInfluencers);

  const { t } = useI18n();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { isDummyUser } = userContext;
  const resolvedInfluencers = React.useMemo(() => {
    if (isDummyUser) return sampleData;
    return topPerformingInfluencers;
  }, [topPerformingInfluencers, isDummyUser]);

  const columns = [
    {
      title: t('auth.influencer'),
      dataIndex: 'influencer',
      key: 'influencer',
      width: '100%',
      fontWeight: '400',
      render: (item: any) => (
        <div className="flex items-center gap-2">
          <img
            src={
              item.profileUrl
                ? item.profileUrl
                : item.profileImage
                  ? item.profileImage
                  : '/assets/profilePicture.png'
            }
            alt={String(t('campaign.profile.profileImage'))}
            className="w-8 h-8 rounded-full"
          />
          <span>
            {[item.firstName, item.lastName].filter(Boolean).join(' ') ||
              item.userName ||
              '—'}
          </span>{' '}
        </div>
      ),
    },
    {
      title: t('common.totalSubmission'),
      dataIndex: 'postCount',
      key: 'postCount',
      width: '100%',
      fontWeight: '400',
    },

    {
      title: t('common.socialScoring'),
      dataIndex: 'socialScoring',
      key: 'socialScoring',
      width: '100%',
      render: (item: any) => (
        <div>
          <div className="bg-[#E6F8F0] rounded-full px-2 py-1 text-[#00AE3E] w-min text-xs font-medium">
            {item?.socialScore?.totalScore ? item?.socialScore?.totalScore : 0}
            /1000
          </div>
        </div>
      ),
    },
    {
      title: t('common.topPlatform'),
      dataIndex: 'platform',
      key: 'platform',
      width: '100%',
      fontWeight: '400',
      render: (item: any) => (
        <div className="text-[#212121] text-sm font-normal leading-[22px] tracking-[0.01em]">
          {(item.platforms?.length ? item.platforms : [item.platform])
            .map((p: string) => formatPlatformLabel(p))
            .join(' & ')}
        </div>
      ),
    },

    {
      title: (
        <div className="flex items-center gap-1">
          <span>{t('common.totalViews')}</span>
          <InfoTooltip text="Times content was shown" position="top" />
        </div>
      ),
      dataIndex: 'totalViews',
      key: 'totalViews',
      width: '100%',
      fontWeight: '400',
      render: (item: any) => <div>{formatCount(item.views ?? 0)}</div>,
    },
    {
      title: t('campaign.actions.view'),
      dataIndex: 'view',
      key: 'view',
      width: '100%',
      render: (item: any) => (
        <div>
          <img src="/assets/eye.svg" alt={String(t('campaign.actions.view'))} />
        </div>
      ),
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    console.log(`Page changed to: ${page}`);
  };

  const startIndex = (currentPage - 1) * pageSize;
  console.log('startIndex', startIndex);
  console.log('pageSize', pageSize);

  const endIndex = startIndex + pageSize;
  const currentData = resolvedInfluencers.slice(startIndex, endIndex);

  return (
    <div className="font-header">
      <h2 className="text-sm font-medium text-[#121111] opacity-90 mb-4">
        {t('common.topPerformingInfluencers')}
      </h2>
      <div>
        {resolvedInfluencers?.length === 0 ? (
          <div className="flex items-center justify-center min-h-[50px]">
            <span className="text-xl font-medium text-[#9B919D]">No Data</span>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={currentData}
            pagination={{
              total: resolvedInfluencers.length,
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
            }}
            onRowClick={(record: any) => {
              navigate(ROUTES.CREATOR_SUBMISSIONS, {
                state: {
                  userId: record.userId,
                  fromMenu: 'campaign',
                  fromTab: 'analytics',
                },
              });
            }}
            rowClassName={'cursor-pointer'}
          />
        )}
      </div>
    </div>
  );
};

export default TopPerformingInfluencersComponent;
